import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Divider,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  TextField,
  Backdrop,
  CircularProgress,
  Tooltip,
  IconButton,
  Alert,
} from "@mui/material";
import { ArrowForward, Send as SendIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  getAnswer,
  getCarrierByBusinessName,
} from "../../redux/action/carrier";
import Header from "../header/Header";
import { setTheme } from "../../redux/reducers/themeSlice";
import { getMovesByPhoneNumber } from "../../redux/action/move-service";

const MainScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading data...");
  const [carrierEmail, setCarrierEmail] = useState("");
  const { businessName } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  const [phoneNumber, setPhoneNumber] = useState<any>(null);
  const [phoneError, setPhoneError] = useState("");
  const [message, setMessage] = useState("");
  const [carrierCanonicalId, setCarrierCanonicalId] = useState("");

  const isValidColor = (color: string): boolean => {
    const colorRegex =
      /^#([0-9A-Fa-f]{3}){1,2}$|^(rgb|rgba|hsl|hsla)\([^\)]*\)$/;
    return colorRegex.test(color);
  };

  useEffect(() => {
    if (businessName) {
      dispatch<any>(getCarrierByBusinessName(businessName))
        .then(unwrapResult)
        .then((res: any) => {
          setCarrierEmail(res.email);
          setCarrierCanonicalId(res.canonical_id);
          if (res.color_hex && isValidColor(res.color_hex)) {
            dispatch(
              setTheme({
                primaryColor: res.color_hex,
                secondaryColor: "#A8E6FF",
              })
            );
          }
        })
        .catch((err: any) => {
          console.error("Failed to fetch carrier data:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [businessName, dispatch]);

  useEffect(() => {
    if (businessName) {
      localStorage.setItem("businessName", businessName);
    }
    if (carrierEmail) {
      localStorage.setItem("email", carrierEmail);
    }
  }, [businessName]);

  const [conversation, setConversation] = useState<any>([]);

  const handleAskQuestion = async () => {
    if (!question || !carrierEmail) return;

    setIsLoading(true);
    setLoadingMessage("Analyzing your question...");
    try {
      const result = await dispatch<any>(
        getAnswer({
          email: carrierEmail,
          question,
        })
      );
      const response = unwrapResult(result);
      setAnswer(response.answer);
      setConversation((prev: any) => [
        ...prev,
        {
          customerQuery: question,
          AiResponse: response.answer,
        },
      ]);
      setQuestion("");
    } catch (error) {
      console.error("Error getting answer:", error);
      setAnswer("Sorry, I couldn't process your question. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleServiceClick = (service: string) => {
    if (service === "Moving Services+" && businessName) {
      navigate(`/${businessName}/${carrierCanonicalId}/choose-service`);
    } else if (service === "Labor and Packing Only") {
      navigate(`/${businessName}/labour-and-packing`);
    } else if (service === "Junk Removal") {
      navigate(`/${businessName}/junk-removal`);
    } else if (service === "Previous Customer") {
      navigate(`/${businessName}/${carrierCanonicalId}/consumer-login`);
    } else {
      console.log(`Service ${service} is disabled`);
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = value.replace(/\D/g, "");
    if (formattedValue.length <= 10) {
      setPhoneNumber(formattedValue);
      setPhoneError("");
    }
    if (formattedValue && formattedValue.length < 10) {
      setMessage("Phone number must be 10 digits");
      return;
    } else {
      setMessage("");
    }
  };

  const validatePhoneNumber = () => {
    if (phoneNumber.length !== 10) {
      setPhoneError("Phone number must be 10 digits");
      return false;
    }
    return true;
  };

  const handleFindMove = async () => {
    if (!validatePhoneNumber()) return;

    setIsLoading(true);
    setLoadingMessage("Searching for your moves...");

    try {
      await dispatch<any>(getMovesByPhoneNumber(phoneNumber))
        .then(unwrapResult)
        .then((res: any) => {
          const lastStep = res.last_step;
          const moveCanonicalId = res.canonical_id;
          switch (lastStep) {
            case 1:
              navigate(`/${businessName}/choose-service`);
              break;
            case 2:
              navigate(`/${businessName}/delivery-details/${moveCanonicalId}`);
              break;
            case 3:
              navigate(`/${businessName}/apartment-info/${moveCanonicalId}`);
              break;
            case 4:
              navigate(`/${businessName}/bulky-items/${moveCanonicalId}`);
              break;
            case 5:
              navigate(`/${businessName}/insurance/${moveCanonicalId}`);
              break;
            case 6:
              navigate(`/${businessName}/user-details/${moveCanonicalId}`);
              break;
            case 7:
              navigate(`/${businessName}/storage/${moveCanonicalId}`);
              break;
            case 8:
              navigate(`/${businessName}/upload-items/${moveCanonicalId}`);
              break;
            case 9:
              navigate(`/${businessName}/inventory/${moveCanonicalId}`);
              break;
            case 10:
              navigate(`/${businessName}/move-price/${moveCanonicalId}`);
              break;
            case 11:
              navigate(`/${businessName}/move-summary/${moveCanonicalId}`);
              break;
            default:
              setMessage("No Pending Move Found");
              return;
          }
        });
    } catch (error) {
      console.error("Error fetching moves:", error);
      setMessage("No move found for this phone number");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversation]);

  return (
    <>
      <Header />
      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "20px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
        }}
      >
        {/* Loader Section */}
        {isLoading && (
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backdropFilter: "blur(5px)",
              backgroundColor: "rgba(0,0,0,0.2)",
            }}
            open={isLoading}
          >
            <CircularProgress size={50} color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {loadingMessage}
            </Typography>
          </Backdrop>
        )}

        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography fontSize={16} mb={1} fontWeight={600}>
              Want to continue where you last left? Let’s find your move.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Enter your phone number"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                error={!!phoneError}
                helperText={phoneError}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>+1</Typography>,
                  inputProps: {
                    maxLength: 10,
                    pattern: "[0-9]*",
                  },
                }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleFindMove}
                disabled={isLoading || phoneNumber?.length !== 10}
                sx={{
                  height: "56px",
                  minWidth: "120px",
                  borderRadius: "8px",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: "600",
                  "&:hover": { backgroundColor: "#357ABD" },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                  },
                }}
              >
                {isLoading ? "Searching..." : "Find Move"}
              </Button>
            </Box>
            {message && <Alert severity="error">{message}</Alert>}
          </Box>

          <Typography
            fontWeight={700}
            fontSize={isMobile ? "16px" : "18px"}
            textAlign="center"
            mb={2}
          >
            Select Service Type
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

          <List sx={{ mb: 2 }}>
            {[
              "Moving Services+",
              // "Labor and Packing Only",
              // "Junk Removal",
              "Previous Customer",
            ].map((service, index) => (
              <ListItem
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  marginBottom: "8px",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => handleServiceClick(service)}
              >
                <ListItemText
                  primary={service}
                  primaryTypographyProps={{
                    fontSize: 16,
                    fontWeight: 500,
                  }}
                />
                <IconButton size="small">
                  <ArrowForward fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />
          <Typography fontSize={16} mb={2} fontWeight={600}>
            Ask a Question?
          </Typography>
          {conversation.length > 0 && (
            <Box
              ref={containerRef}
              sx={{
                mt: 1,
                mb: 2,
                height: "200px",
                overflowY: "auto",
                pr: 1,
                scrollBehavior: "smooth",
              }}
            >
              {conversation.map((item: any, index: any) => {
                const isLast = index === conversation.length - 1;
                return (
                  <Box
                    key={index}
                    ref={isLast ? lastMessageRef : null}
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: "10px",
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      boxShadow: theme.shadows[1],
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        mb: 1,
                      }}
                    >
                      You:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, pl: 2 }}>
                      {item.customerQuery}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        mb: 1,
                      }}
                    >
                      Response:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "pre-line",
                        lineHeight: 1.6,
                        pl: 2,
                      }}
                    >
                      {item.AiResponse}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          <TextField
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAskQuestion()}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
            InputProps={{
              endAdornment: (
                <Tooltip title="Send question">
                  <IconButton
                    onClick={handleAskQuestion}
                    disabled={isLoading || !question}
                    color="primary"
                    sx={{
                      position: "absolute",
                      right: 8,
                      bottom: 8,
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                      },
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "20px",
            marginTop: "20px",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: "20px",
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
            }}
            disabled
          >
            Back
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: "20px",
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
              "&:hover": { backgroundColor: "#357ABD" },
            }}
            onClick={handleAskQuestion}
            disabled={isLoading || !question}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isLoading ? "Processing..." : "Ask Question"}
          </Button>
        </Box>
        <Box mt={2} textAlign="center">
          <Typography variant="body2">
            Existing Booking?
            <Typography
              component="span"
              color="primary"
              fontWeight={600}
              sx={{ cursor: "pointer" }}
              onClick={() =>
                navigate(
                  `/${businessName}/${carrierCanonicalId}/consumer-login`
                )
              }
            >
              Login Here
            </Typography>
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default MainScreen;
